import React from 'react';
import {SegmentedControl} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';

export interface ConditionOperatorPickerProps {
	options: {value: string; label: string}[];
	value: string;
	onChange: (operator: string) => void;
}

/** The include/exclude choice for one condition, rendered above the value list.
 *
 * Renders nothing for a single option: one choice is not a choice, and a field whose catalog entry
 * declares only one family genuinely has nothing to offer here. `value` is the STORED operator's
 * family representative (IS, IS_NOT, IS_ALL, IS_NOT_ALL) — the caller maps the stored operator
 * (which may be a multi-value member like IN or NOT_IN) onto its family before passing it here,
 * since `options` is built from those representatives too. */
export function ConditionOperatorPicker({options, value, onChange}: ConditionOperatorPickerProps): JSX.Element | null {
	const {formatMessage} = useIntl();

	if (options.length < 2) {
		return null;
	}

	return (
		<SegmentedControl
			label={formatMessage({id: 'ai_report_builder.conditions.operator_label', defaultMessage: 'Condition'})}
			value={value}
			onValueChange={next => {
				// SegmentedControl reports a click on the ALREADY-selected segment as a deselect and
				// emits an empty string. An operator is not an optional choice - every condition has
				// one - so that is a no-op here. Forwarded, it would write `operator: ''` into the
				// definition: invalid against the contract, unresolvable by `operatorLabel`, and
				// rejected on save. The reader's click looks like it did nothing, which is correct,
				// because deselecting the only chosen operator is not a state a condition can be in.
				//
				// Testing membership rather than truthiness also covers the case where `value` names
				// no rendered segment - a stored operator outside the four families leaves the control
				// with nothing checked - so the first click cannot commit a value this picker never
				// offered.
				if (!options.some(option => option.value === next)) {
					return;
				}
				onChange(next);
			}}
			data-cy={'ai-report-condition-operator'}
		>
			{options.map(option => (
				<SegmentedControl.Item
					key={option.value}
					value={option.value}
					label={option.label}
					data-cy={`ai-report-condition-operator-${option.value}`}
				>
					{option.label}
				</SegmentedControl.Item>
			))}
		</SegmentedControl>
	);
}
